import { VehicleType, VehicleCategory, LicensePlateState } from "@app.automotus.io/components/common";
import { VehicleCategoryValue, VehicleTypeValue } from "@app.automotus.io/components/common";
import { NewVehicleRow } from "./vehicles.types";

export type ImportFormat = "tsv" | "csv";

// Accepts both human-readable labels ("Pickup Truck") and raw values ("pickup_truck")
const VEHICLE_TYPE_MAP: Record<string, VehicleTypeValue> = VehicleType.All.reduce(
  (acc, vt) => ({
    ...acc,
    [vt.label.toLowerCase()]: vt.value,
    [vt.value.toLowerCase()]: vt.value,
  }),
  {} as Record<string, VehicleTypeValue>,
);

// Accepts labels ("Commercial") and values ("commercial") from VehicleCategory
const CATEGORY_MAP: Record<string, VehicleCategoryValue> = VehicleCategory.All.reduce(
  (acc, vc) => ({
    ...acc,
    [vc.label.toLowerCase()]: vc.value,
    [vc.value.toLowerCase()]: vc.value,
  }),
  {} as Record<string, VehicleCategoryValue>,
);

// Accepts full state names ("California") and abbreviations ("CA" / "ca")
const STATE_MAP: Record<string, string> = LicensePlateState.All.reduce(
  (acc, s) => ({
    ...acc,
    [s.fullName.toLowerCase()]: s.abbreviation,
    [s.abbreviation.toLowerCase()]: s.abbreviation,
  }),
  {} as Record<string, string>,
);

function mapTypeLabel(raw: string): VehicleTypeValue | "" {
  return VEHICLE_TYPE_MAP[raw.toLowerCase().trim()] ?? "";
}

function mapCategoryLabel(raw: string): VehicleCategoryValue {
  return CATEGORY_MAP[raw.toLowerCase().trim()] ?? "commercial";
}

function mapStateCode(raw: string): string {
  const trimmed = raw.trim();
  return STATE_MAP[trimmed.toLowerCase()] ?? trimmed.toUpperCase();
}

const HEADER_VALUES = new Set([
  "state",
  "state code",
  "license plate state",
  "plate number",
  "plate",
  "license plate number",
  "make",
  "vehicle make",
  "model",
  "vehicle model",
  "type",
  "vehicle type",
  "category",
]);

function isHeaderRow(line: string, delimiter: string): boolean {
  // Strip surrounding quotes that Excel adds to CSV fields (e.g. "State" → state)
  const firstCol = line.split(delimiter)[0].trim().replace(/^"|"$/g, "").toLowerCase();
  return HEADER_VALUES.has(firstCol);
}

/**
 * Parses raw TSV (Excel paste) or CSV text into NewVehicleRow objects.
 * Column order: State, Plate Number, Make, Model, Type, Category (optional)
 * Automatically skips a header row if one is present.
 */
export function parseImportText(text: string, format: ImportFormat): NewVehicleRow[] {
  const delimiter = format === "csv" ? "," : "\t";
  // Strip UTF-8 BOM and normalize Windows line endings (both common in Excel CSV exports)
  const normalized = text
    .replace(/\uFEFF/u, "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
  const lines = normalized.split("\n").filter((line) => line.trim().length > 0);

  const dataLines = lines.length > 0 && isHeaderRow(lines[0], delimiter) ? lines.slice(1) : lines;

  return dataLines.map((line, index) => {
    const cols = line.split(delimiter);
    const [stateCode = "", rawPlate = "", make = "", model = "", rawType = "", rawCategory] = cols;

    return {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}-${index}`,
      stateCode: mapStateCode(stateCode),
      licensePlate: rawPlate.replace(/\s/g, "").toUpperCase(),
      make: make.trim(),
      model: model.trim(),
      type: mapTypeLabel(rawType),
      category: rawCategory !== undefined ? mapCategoryLabel(rawCategory) : "commercial",
      errors: {},
    };
  });
}
